<template>
  <div class="pc-sidebar" :class="{ 'pc-sidebar-hide': isSidebarCollapsed }">
    <div class="pc-sidebar-header">
      <a href="#" class="pc-sidebar-logo">
        <img src="@/assets/images/logo/vesproc220x66.png" alt="Vesproc Supplier" class="logo-lg" />
      </a>
    </div>
    <div class="pc-sidebar-content">
      <ul class="pc-navbar">
        <!-- Standard menu items (hidden for buyer tenants) -->
        <template v-if="!isBuyerTenant">
          
          <!-- Procurement section -->
          <template v-if="canAccessRfq() || canAccessPurchaseOrders()">
            <li class="pc-item pc-caption">
              <label>{{ $t('common.navigation.procurement') }}</label>
            </li>
            
            <!-- RFQ menu (only for users with quotation permissions) -->
            <li v-if="canAccessRfq() "  class="pc-item">
              <router-link class="pc-link" :class="{ 'active': $route.path === '/rfq' }" to="/rfq">
                <span class="pc-micon">
                  <i class="ph-duotone ph-file-text"></i>
                </span>
                <span class="pc-mtext">{{ $t('common.navigation.rfq') }}</span>
              </router-link>
            </li>
            
            <!-- Purchase Order menu (only for users with purchase order permissions) -->
            <li v-if="canAccessPurchaseOrders()" class="pc-item">
              <router-link class="pc-link" :class="{ 'active': $route.path === '/purchase-order' }" to="/purchase-order">
                <span class="pc-micon">
                  <i class="ph-duotone ph-shopping-cart"></i>
                </span>
                <span class="pc-mtext">{{ $t('common.navigation.purchaseOrder') }}</span>
              </router-link>
            </li>
          </template>
          
          <!-- Admin Panel menu (only for super admin users, not when impersonating) -->
          <li v-if="currentUser?.isSuperAdmin && !currentUser?.isImpersonating" class="pc-item">
            <router-link class="pc-link" :class="{ 'active': $route.path === '/admin' }" to="/admin">
              <span class="pc-micon">
                <i class="ph-duotone ph-crown"></i>
              </span>
              <span class="pc-mtext">{{ $t('common.navigation.admin') }}</span>
            </router-link>
          </li>
          
          <!-- 2FA Setup menu (only for super admin users, not when impersonating) -->
          <li v-if="currentUser?.isSuperAdmin && !currentUser?.isImpersonating" class="pc-item">
            <router-link class="pc-link" :class="{ 'active': $route.path === '/auth/2fa-setup' }" to="/auth/2fa-setup">
              <span class="pc-micon">
                <i class="ph-duotone ph-shield-plus"></i>
              </span>
              <span class="pc-mtext">{{ $t('auth.twoFactor.setup') }}</span>
            </router-link>
          </li>
          
          <!-- Databank menu (only for super admin users, not when impersonating) -->
          <li v-if="currentUser?.isSuperAdmin && !currentUser?.isImpersonating" class="pc-item">
            <router-link class="pc-link" :class="{ 'active': $route.path === '/databank' }" to="/databank">
              <span class="pc-micon">
                <i class="ph-duotone ph-database"></i>
              </span>
              <span class="pc-mtext">{{ $t('common.navigation.databank') }}</span>
            </router-link>
          </li>
          
        </template>
        
        <!-- LookUp menu (only for buyer tenants) -->
        <li v-if="isBuyerTenant" class="pc-item">
          <router-link class="pc-link" :class="{ 'active': $route.path === '/lookup' }" to="/lookup">
            <span class="pc-micon">
              <i class="ph-duotone ph-magnifying-glass"></i>
            </span>
            <span class="pc-mtext">{{ $t('common.navigation.lookup') }}</span>
          </router-link>
        </li>
        
        <!-- Mail Tracking menu (only for buyer tenants) -->
        <li v-if="isBuyerTenant" class="pc-item">
          <router-link class="pc-link" :class="{ 'active': $route.path.startsWith('/mail-tracking') }" to="/mail-tracking">
            <span class="pc-micon">
              <i class="ph-duotone ph-envelope-open"></i>
            </span>
            <span class="pc-mtext">{{ $t('common.navigation.mailTracking') }}</span>
          </router-link>
        </li>
        
        <!-- Quotation Mail Recipients menu (only for buyer tenants) -->
        <li v-if="isBuyerTenant" class="pc-item">
          <router-link class="pc-link" :class="{ 'active': $route.path === '/quotation-mail-recipients' }" to="/quotation-mail-recipients">
            <span class="pc-micon">
              <i class="ph-duotone ph-envelope-simple"></i>
            </span>
            <span class="pc-mtext">{{ $t('common.navigation.quotationMailRecipients') || 'Quotation Mail Recipients' }}</span>
          </router-link>
        </li>
        
        <!-- Account section (hidden for super admin when not impersonating) -->
        <template v-if="shouldShowAccountSection">
          <li class="pc-item pc-caption">
            <label>{{ $t('common.navigation.account') }}</label>
          </li>
          
          <!-- Profile menu (only for non-buyer tenants) -->
          <li v-if="!isBuyerTenant" class="pc-item">
            <router-link class="pc-link" :class="{ 'active': $route.path === '/profile' }" to="/profile">
              <span class="pc-micon">
                <i class="ph-duotone ph-buildings"></i>
              </span>
              <span class="pc-mtext">{{ $t('common.navigation.profile') }}</span>
            </router-link>
          </li>
          
          <!-- Users menu (only for standard users with permission, not buyer tenants) -->
          <li v-if="!isBuyerTenant && hasAnyPermission(['Supplier.Users', 'Supplier.Administration'])" class="pc-item">
            <router-link class="pc-link" :class="{ 'active': $route.path.startsWith('/users') }" to="/users">
              <span class="pc-micon">
                <i class="ph-duotone ph-users"></i>
              </span>
              <span class="pc-mtext">{{ $t('common.navigation.users') }}</span>
            </router-link>
          </li>
          
          <li class="pc-item">
            <router-link class="pc-link" :class="{ 'active': $route.path === '/settings' }" to="/settings">
              <span class="pc-micon">
                <i class="ph-duotone ph-gear-six"></i>
              </span>
              <span class="pc-mtext">{{ $t('common.navigation.settings') }}</span>
            </router-link>
          </li>
          
          <!-- Help menu (visible for lookup users and supplier users, hidden for admin unless impersonating) -->
          <li v-if="shouldShowHelpMenu" class="pc-item">
            <router-link class="pc-link" :class="{ 'active': $route.path === '/help' }" to="/help">
              <span class="pc-micon">
                <i class="ph-duotone ph-question"></i>
              </span>
              <span class="pc-mtext">{{ $t('common.navigation.help') }}</span>
            </router-link>
          </li>
        </template>
        
        <li class="pc-item">
          <a href="#" class="pc-link" @click.prevent="logout">
            <span class="pc-micon">
              <i class="ph-duotone ph-sign-out"></i>
            </span>
            <span class="pc-mtext">{{ $t('common.navigation.logout') }}</span>
          </a>
        </li>
        
        <!-- Debug section (remove in production) -->
      
      </ul>
    </div>
  </div>
</template>

<script>
import { computed } from 'vue';
import { useStore } from 'vuex';
import { useI18n } from 'vue-i18n';
import { usePermissions } from '@/composables/usePermissions';
import { useRfqPermissions } from '@/composables/useRfqPermissions';
import { usePurchaseOrderPermissions } from '@/composables/usePurchaseOrderPermissions';
import Swal from 'sweetalert2';

export default {
  name: 'AppSidebar',
  props: {
    isSidebarCollapsed: {
      type: Boolean,
      default: false
    }
  },
  setup() {
    const store = useStore();
    const { t } = useI18n();
    const { userPermissions, hasPermission, hasAnyPermission } = usePermissions();
    const { canAccessRfq } = useRfqPermissions();
    const { canAccessPurchaseOrders } = usePurchaseOrderPermissions();
    
    const currentUser = computed(() => store.getters['auth/currentUser']);
    
    // Debug logging for super admin detection
    
    
    // Debug logging for permissions
    
    
    // Check if we're in development mode
    const isDevelopment = computed(() => import.meta.env.DEV);
    
    // Check if current user is a buyer tenant based on editionId
    const isBuyerTenant = computed(() => {
      const buyerTenantEditionId = '98CAF202-9D8E-B62F-01C1-39FF3E9F71A0';

      
      // Check both user object and localStorage for editionId
      const userEditionId = currentUser.value?.editionId || localStorage.getItem('editionId');

      
      // Case-insensitive comparison
      const isMatch = userEditionId?.toUpperCase() === buyerTenantEditionId.toUpperCase();

      
      return isMatch;
    });

    // Check if help menu should be shown
    // Show for lookup users and supplier users, hide for admin unless impersonating
    const shouldShowHelpMenu = computed(() => {
      const user = currentUser.value;
      // Show for all users except super admin when not impersonating
      return !(user?.isSuperAdmin && !user?.isImpersonating);
    });

    // Check if account section should be shown
    // Hide for super admin when not impersonating
    const shouldShowAccountSection = computed(() => {
      const user = currentUser.value;
      
      // Show for non-super admin users
      if (!user?.isSuperAdmin) {
        return true;
      }
      
      // Show for super admin only when impersonating
      return user?.isImpersonating === true;
    });
    
    const logout = async () => {
      try {
        // Show confirmation dialog
        const result = await Swal.fire({
          title: t('common.confirmations.logout.title', 'Logout'),
          text: t('common.confirmations.logout.message', 'Are you sure you want to logout?'),
          icon: 'question',
          showCancelButton: true,
          confirmButtonColor: '#3085d6',
          cancelButtonColor: '#6c757d',
          confirmButtonText: t('common.confirmations.logout.confirm', 'Yes, logout'),
          cancelButtonText: t('common.confirmations.logout.cancel', 'Cancel'),
          customClass: {
            confirmButton: 'btn btn-primary',
            cancelButton: 'btn btn-outline-secondary ml-1'
          },
          buttonsStyling: false
        });
        
        if (!result.isConfirmed) {
          return; // User cancelled logout
        }
        
        await store.dispatch('auth/logout');
        
        // Use window.location.href for a clean redirect that prevents any pending API calls
        window.location.href = '/login';
      } catch (error) {
        console.error('Logout error:', error);
        // Even on error, redirect to login page
        window.location.href = '/login';
      }
    };

    
    return {
      currentUser,
      isBuyerTenant,
      shouldShowHelpMenu,
      shouldShowAccountSection,
      hasPermission,
      hasAnyPermission,
      userPermissions,
      logout,
      isDevelopment,
      canAccessRfq,
      canAccessPurchaseOrders
    };
  }
};
</script>

<style scoped>
.pc-sidebar {
  width: 260px;
  height: 100vh;
  position: fixed;
  top: 0;
  left: 0;
  background-color: var(--bs-white);
  box-shadow: 0 1px 20px 0 rgba(0, 0, 0, 0.1);
  z-index: 1029;
  transition: all 0.3s ease;
}

.pc-sidebar-header {
  padding: 15px 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 80px;
  border-bottom: 1px solid #f1f3f4;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
}

.pc-sidebar-logo {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  text-decoration: none;
  padding: 8px 15px;
  width: 100%;
  transition: all 0.3s ease;
}

.pc-sidebar-logo:hover {
  transform: translateY(-1px);
  text-decoration: none;
}

.logo-lg {
  height: 50px;
  width: auto;
  max-width: 200px;
  object-fit: contain;
  object-position: left;
  display: block;
  margin: 0;
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .pc-sidebar-header {
    padding: 10px 15px;
    min-height: 60px;
  }
  
  .pc-sidebar-logo {
    padding: 8px 12px;
  }
  
  .logo-lg {
    height: 36px;
    max-width: 150px;
  }
}

.pc-navbar {
  list-style: none;
  margin: 0;
  padding: 0;
}

.pc-item {
  width: 100%;
  margin-bottom: 6px;
}

.pc-caption {
  padding: 20px 0 5px 15px;
}

.pc-caption label {
  font-size: 12px;
  color: var(--bs-secondary-color);
  font-weight: 500;
  text-transform: uppercase;
  margin-bottom: 0;
}

.pc-link {
  display: flex;
  align-items: center;
  padding: 10px 15px;
  border-radius: 0.25rem;
  color: var(--bs-body-color);
  text-decoration: none;
  transition: all 0.2s;
  margin: 0 10px;
}

.pc-link:hover {
  background-color: rgba(var(--bs-primary-rgb), 0.08);
  color: var(--bs-primary);
}

.pc-link.active {
  background-color: rgba(var(--bs-primary-rgb), 0.08);
  color: var(--bs-primary);
  font-weight: 500;
}

.pc-micon {
  margin-right: 10px;
  font-size: 20px;
  width: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.pc-micon i {
  display: flex;
  align-items: center;
  line-height: 1;
}

.pc-mtext {
  font-size: 14px;
  line-height: 20px;
}

</style> 